import { pxToRem } from '@fluentsms/agentnet-web-components';
import { CountryCode, isUS, requiresStateAndPostalCode, isUSTerritory } from '@agentnet/utils';
import {
  Box,
  Checkbox,
  createStyles,
  FormControlLabel,
  Grid,
  makeStyles,
  Switch,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import { isValid, parse } from 'date-fns';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import type { ChangeEvent, FC } from 'react';
import { useContext, useEffect, useState } from 'react';

import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import RadioGroup from 'ui-kit/components/radios/RadioGroup';
import { AddressFields } from 'ui-kit/form/interfaces/AddressModal';
import { AgentNetDropdownSelector, CurrencyField } from 'ui-kit/inputs';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import territories from 'ui-kit/inputs/geo-data/us-territory.json';
import canadaProvinces from 'ui-kit/inputs/geo-data/canada-provinces.json';
import mexicoStates from 'ui-kit/inputs/geo-data/mexico-states.json';
import foreignTerritories from 'ui-kit/inputs/geo-data/foreign-territory.json';
import GoogleAutocompleteSearch from 'ui-kit/inputs/GoogleAutocompleteSearch/GoogleAutocompleteSearch';
import states from 'ui-kit/inputs/StateSelectField/states.json';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import USPostalCodeField from 'ui-kit/inputs/USPostalCodeField/USPostalCodeField';
import { FieldValidationError } from 'utilities/validation/validation';
import { IdentificationNumberField } from '../../common/IdentificationNumberField';
import { Transferee, TransfereeParty } from '../../types';
import {
  addressTypeOptions,
  baseIdentificationTypeOptions,
  foreignTerritoryOptions,
  TransfereeType,
} from '../constants';
import { getInitialPartyData } from '../models/party';
import { TransfereeFormProps } from '../types';
import PartyAccordion from './PartyAccordion';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      width: '100%',
      '& .MuiGrid-container': {
        width: '100%',
        margin: 0,
      },
    },
    formField: {
      marginBottom: theme.spacing(0),
      flexBasis: '50%',
      maxWidth: '50%',
    },
    addressContainer: {
      marginTop: theme.spacing(1),
    },
    formLabel: {
      fontSize: pxToRem(14),
      marginBottom: theme.spacing(1),
      fontWeight: 500,
    },
    formControlLabel: {
      marginLeft: 0,
      marginBottom: theme.spacing(1),
      '& .MuiTypography-root': {
        fontSize: pxToRem(14),
        color: theme.palette.text.primary,
      },
    },
    radioGroup: {
      marginTop: theme.spacing(1),
    },
    sectionContainer: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(-1),
    },
    sectionHeader: {
      fontSize: pxToRem(18),
      fontWeight: 600,
      marginBottom: theme.spacing(1),
      color: theme.palette.text.primary,
      marginLeft: theme.spacing(1),
    },
    switch: {
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(-0.5),
    },
    infoIcon: {
      fontSize: pxToRem(18),
      marginLeft: theme.spacing(2),
      color: theme.palette.grey[600],
      verticalAlign: 'middle',
    },
    dropdown: {
      width: '100%',
      '& .MuiInputLabel-formControl': {
        fontSize: pxToRem(14),
      },
    },
    addButton: {
      marginRight: theme.spacing(1),
    },
    addIcon: {
      fontSize: pxToRem(16),
    },
    errorMessage: {
      marginLeft: 14,
      marginTop: 3,
      display: 'block',
    },
    dropdownError: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.palette.error.main} !important`,
      },
    },
  }),
);

const getIdentificationTypeOptions = (transfereeType: TransfereeType) => {
  if (transfereeType === TransfereeType.TRUST || transfereeType === TransfereeType.ENTITY) {
    return baseIdentificationTypeOptions.filter((option) => option.value !== 'foreignPassport');
  }
  return baseIdentificationTypeOptions;
};

const TransfereeForm: FC<TransfereeFormProps> = ({
  transferee,
  index,
  onUpdate,
  validationErrors: contextValidationErrors,
  clearValidationErrors,
}) => {
  const classes = useStyles();
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { generateButtonClicked, setTransfereeValidationErrors } = fincenReportCtx ?? {};

  const [formData, setFormData] = useState<Transferee>({
    ...transferee,
    transfereeType: transferee.transfereeType || TransfereeType.ENTITY,
  });

  const [expandedPartyId, setExpandedPartyId] = useState<string | null>(null);
  const [alternateNames, setAlternateNames] = useState<string[]>(() => {
    return transferee.transfereeDbaName ? [transferee.transfereeDbaName] : [''];
  });
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  const effectiveValidationErrors =
    contextValidationErrors && contextValidationErrors.length > 0 ? contextValidationErrors : validationErrors;

  const clearFieldError = (fieldName: string) => {
    if (!setTransfereeValidationErrors) {
      return;
    }

    // Use whichever error source has errors
    const errorsToFilter =
      contextValidationErrors && contextValidationErrors.length > 0 ? contextValidationErrors : validationErrors || [];

    const filteredErrors = errorsToFilter.filter((error) => {
      // Generate all possible field name patterns to match
      const patterns = [`[${index}].${fieldName}`, fieldName];

      // Check if error matches any pattern
      const matches = patterns.some((pattern) => error.field === pattern || error.field.endsWith(`.${fieldName}`));

      return !matches;
    });
    if (filteredErrors.length !== errorsToFilter.length) {
      setTransfereeValidationErrors(filteredErrors);
    }
  };
  useEffect(() => {
    const updatedTransferee = {
      ...transferee,
      transfereeType: transferee.transfereeType || TransfereeType.ENTITY,
    };

    let hasChanges = false;

    // For Entity type: if foreignBusinessNoUSLocation is false and addressType is empty, default to 'foreign'
    if (
      updatedTransferee.transfereeType === TransfereeType.ENTITY &&
      !updatedTransferee.foreignBusinessNoUSLocation &&
      !updatedTransferee.addressType
    ) {
      updatedTransferee.addressType = 'foreign';
      hasChanges = true;
    }

    // Automatically add one party if transferee is Entity or Trust and has no parties
    if (
      (updatedTransferee.transfereeType === TransfereeType.ENTITY ||
        updatedTransferee.transfereeType === TransfereeType.TRUST) &&
      (!updatedTransferee.parties || updatedTransferee.parties.length === 0)
    ) {
      const newPartyId = `party-${index}-0`;
      const newParty: TransfereeParty = {
        ...getInitialPartyData(),
        id: newPartyId,
        foreignBusinessNoUSLocation: false,
        addressType: '',
        address: {
          address1: '',
          address2: '',
          city: '',
          state: '',
          country: CountryCode.US,
          postalCode: '',
        },
      };
      updatedTransferee.parties = [newParty];
      hasChanges = true;
    }

    if (hasChanges) {
      onUpdate(updatedTransferee, index);
    }

    setFormData(updatedTransferee);

    const currentDbaString = alternateNames.filter((name) => name.trim()).join(', ');
    if (transferee.transfereeDbaName !== currentDbaString) {
      if (transferee.transfereeDbaName) {
        setAlternateNames([transferee.transfereeDbaName]);
      } else if (!transferee.transfereeDbaName && alternateNames.length === 0) {
        setAlternateNames(['']);
      }
    }
  }, [transferee, alternateNames, index, onUpdate]);

  // Clear state errors when country is not US
  useEffect(() => {
    if (formData.primaryUSAddress?.country && formData.primaryUSAddress.country !== CountryCode.US) {
      clearFieldError('primaryUSAddress.state');
    }
    if (formData.foreignBusinessAddress?.country && formData.foreignBusinessAddress.country !== CountryCode.US) {
      clearFieldError('foreignBusinessAddress.state');
    }
    if (formData.foreignPrincipalAddress?.country && formData.foreignPrincipalAddress.country !== CountryCode.US) {
      clearFieldError('foreignPrincipalAddress.state');
    }
  }, [
    formData.primaryUSAddress?.country,
    formData.foreignBusinessAddress?.country,
    formData.foreignPrincipalAddress?.country,
  ]);

  const handleAddAlternateName = () => {
    setAlternateNames([...alternateNames, '']);
  };

  const handleAlternateNameChange = (idx: number, value: string) => {
    const updated = [...alternateNames];
    updated[idx] = value;
    setAlternateNames(updated);

    const dbaNameString = updated.filter((name) => name.trim()).join(', ') || '';

    const updatedFormData = {
      ...formData,
      transfereeDbaName: dbaNameString,
    };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, index);

    clearFieldError('transfereeDbaName');
  };

  const getFieldError = (fieldName: string): FieldValidationError | undefined => {
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;

    // Always show validation errors for date fields (when generateButtonClicked is true)
    if (fieldName === 'dateTrustExecuted') {
      return effectiveValidationErrors?.find(
        (err) =>
          err.field === `[${index}].${fieldName}` ||
          err.field === `transferees[${index}].${fieldName}` ||
          err.field === `transferee[${index}].${fieldName}` ||
          err.field?.includes(`[${index}].${fieldName}`),
      );
    }

    if (fieldName === 'transfereeType' && formData.transfereeType) return undefined;
    const fieldToMatch = `[${index}].${fieldName}`;
    return effectiveValidationErrors?.find((err) => err.field === fieldToMatch);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;

    const updatedFormData = { ...formData, [name]: type === 'checkbox' ? checked : value };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, index);

    clearFieldError(name);
  };

  const handleFieldChange = (field: string, value: string) => {
    const updatedFormData = {
      ...formData,
      [field]: value,
    };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, index);

    clearFieldError(field);
  };

  const handleDateTrustExecutedChange = (date: string | null) => {
    const updatedFormData = {
      ...formData,
      dateTrustExecuted: date || '',
    };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, index);

    // Validate the date format
    if (date && date.trim() !== '') {
      // Check if the date string is complete (not partially filled like 02/__/____)
      const isPartialDate = date.includes('_');

      if (isPartialDate) {
        // If date is incomplete, add validation error
        if (setTransfereeValidationErrors) {
          const newError: FieldValidationError = {
            field: `transferees[${index}].dateTrustExecuted`,
            message: 'Invalid date',
          };
          const updatedErrors = [
            ...(contextValidationErrors || []).filter((err) => err.field !== `transferees[${index}].dateTrustExecuted`),
            newError,
          ];
          setTransfereeValidationErrors(updatedErrors);
        }
      } else {
        // Parse and validate the complete date
        const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
        if (!isValid(parsedDate)) {
          // Add validation error for invalid date
          if (setTransfereeValidationErrors) {
            const newError: FieldValidationError = {
              field: `transferees[${index}].dateTrustExecuted`,
              message: 'Invalid date',
            };
            const updatedErrors = [
              ...(contextValidationErrors || []).filter(
                (err) => err.field !== `transferees[${index}].dateTrustExecuted`,
              ),
              newError,
            ];
            setTransfereeValidationErrors(updatedErrors);
          }
        } else {
          // Check if the date components are within valid ranges
          const parts = date.split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);

            // Validate month (1-12), day (1-31), and year (reasonable range)
            if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
              if (setTransfereeValidationErrors) {
                const newError: FieldValidationError = {
                  field: `transferees[${index}].dateTrustExecuted`,
                  message: 'Invalid date',
                };
                const updatedErrors = [
                  ...(contextValidationErrors || []).filter(
                    (err) => err.field !== `transferees[${index}].dateTrustExecuted`,
                  ),
                  newError,
                ];
                setTransfereeValidationErrors(updatedErrors);
              }
            } else {
              // Clear validation error if date is valid
              clearFieldError('dateTrustExecuted');
            }
          } else {
            // Clear validation error if date is valid
            clearFieldError('dateTrustExecuted');
          }
        }
      }
    } else {
      // Clear validation error if date is empty
      clearFieldError('dateTrustExecuted');
    }
  };

  const handleDropdownChange = (name: string, value: string | number): void => {
    let updatedFormData = {
      ...formData,
      [name]: value,
    };

    if (name === 'identificationType') {
      updatedFormData = {
        ...updatedFormData,
        identificationNumber: '',
      };
    }

    setFormData(updatedFormData);
    onUpdate(updatedFormData, index);

    clearFieldError(name);
  };

  const handleNestedChange = (parentField: keyof Transferee, childField: string, value: string | boolean) => {
    let updatedParentData = {
      ...(formData[parentField] as AddressFields | Record<string, string | boolean>),
      [childField]: value,
    };

    // If changing country, clear state and postalCode when non-US, or auto-set state for US Territories
    if (
      (parentField === 'primaryUSAddress' ||
        parentField === 'foreignBusinessAddress' ||
        parentField === 'foreignPrincipalAddress') &&
      childField === 'country'
    ) {
      const countryValue = (value as string)?.toUpperCase();
      // Auto-set state to territory value for US Territories, reset for other countries
      if (isUSTerritory(countryValue)) {
        updatedParentData = {
          ...updatedParentData,
          state: countryValue,
          postalCode: '',
        };
      } else {
        updatedParentData = {
          ...updatedParentData,
          state: '',
          postalCode: requiresStateAndPostalCode(countryValue) ? (updatedParentData.postalCode as string) || '' : '',
        };
      }
    }

    let updatedFormData = {
      ...formData,
      [parentField]: updatedParentData,
    };

    // Synchronize country between primaryUSAddress and foreignBusinessAddress
    if (childField === 'country' && formData.addressType === 'primary') {
      const countryValue = (value as string)?.toUpperCase();
      if (parentField === 'primaryUSAddress') {
        // Update foreignBusinessAddress country when primaryUSAddress country changes
        updatedFormData = {
          ...updatedFormData,
          foreignBusinessAddress: {
            ...formData.foreignBusinessAddress,
            country: value as string,
          },
        };
      } else if (parentField === 'foreignBusinessAddress') {
        // Update primaryUSAddress country when foreignBusinessAddress country changes
        // Auto-set state for US Territories
        const newState = isUSTerritory(countryValue) ? countryValue : '';
        updatedFormData = {
          ...updatedFormData,
          primaryUSAddress: {
            ...formData.primaryUSAddress,
            country: value as string,
            state: newState,
            postalCode: requiresStateAndPostalCode(countryValue) ? formData.primaryUSAddress?.postalCode || '' : '',
          },
        };
      }
    }

    setFormData(updatedFormData);
    onUpdate(updatedFormData, index);
    clearFieldError(`${parentField}.${childField}`);

    // Clear state and postalCode errors when country changes to one that doesn't require them
    if (childField === 'country') {
      const countryValue = (value as string)?.toUpperCase();
      if (!requiresStateAndPostalCode(countryValue)) {
        // Clear state error for all address fields that have state
        if (
          parentField === 'primaryUSAddress' ||
          parentField === 'foreignBusinessAddress' ||
          parentField === 'foreignPrincipalAddress'
        ) {
          clearFieldError(`${parentField}.state`);
        }
        clearFieldError(`${parentField}.postalCode`);
      }
    }
  };

  const handleAddParty = () => {
    const newPartyId = `party-${index}-${formData.parties?.length || 0}`;
    const newParty: TransfereeParty = {
      ...getInitialPartyData(),
      id: newPartyId,
      foreignBusinessNoUSLocation: false,
      addressType: '',
      address: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: CountryCode.US,
        postalCode: '',
      },
    };

    const updatedFormData = {
      ...formData,
      parties: [...(formData.parties || []), newParty],
    };

    setFormData(updatedFormData);
    setExpandedPartyId(newPartyId);
    onUpdate(updatedFormData, index);
  };

  const handleUpdateParty = (party: TransfereeParty, transfereeIndex: number, partyIndex: number) => {
    const updatedParties = [...(formData.parties || [])];
    updatedParties[partyIndex] = party;

    const updatedFormData = {
      ...formData,
      parties: updatedParties,
    };

    setFormData(updatedFormData);
    onUpdate(updatedFormData, index);
  };

  const handleDeleteParty = (transfereeIndex: number, partyIndex: number) => {
    // Prevent deletion of the first party (index 0)
    if (partyIndex === 0) {
      return;
    }

    const updatedParties = [...(formData.parties || [])];
    updatedParties.splice(partyIndex, 1);

    const updatedFormData = {
      ...formData,
      parties: updatedParties,
    };

    setFormData(updatedFormData);
    onUpdate(updatedFormData, index);
  };

  const territoryOptions = territories.map((territory) => ({
    value: territory.abbreviation,
    label: territory.name,
    name: territory.name,
  }));

  const stateOptions = states.map((state) => ({
    value: state.abbreviation,
    label: state.name,
    name: state.abbreviation,
  }));

  const canadaProvinceOptions = canadaProvinces.map((province) => ({
    name: province.abbreviation,
    value: province.abbreviation,
  }));

  const mexicoStateOptions = mexicoStates.map((state) => ({
    name: state.abbreviation,
    value: state.abbreviation,
  }));

  // Merge all countries for dropdown and remove duplicates
  const allCountryOptions = [
    ...territories.map((t) => ({ name: t.name, value: t.abbreviation })),
    ...foreignTerritories.map((t) => ({ name: t.name, value: t.abbreviation })),
  ]
    .filter((option, index, self) => index === self.findIndex((o) => o.value === option.value))
    .sort((a, b) => {
      const priorityOrder = [
        'United States',
        'American Samoa',
        'Guam',
        'Northern Mariana Islands',
        'Puerto Rico',
        'U.S. Virgin Islands',
        'Canada',
        'Mexico',
      ];
      const aIndex = priorityOrder.indexOf(a.name);
      const bIndex = priorityOrder.indexOf(b.name);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

  // Determine which state/province options to show based on country
  const getStateOptions = (country?: string) => {
    const countryUpper = country?.toUpperCase();
    if (countryUpper === 'US') {
      return stateOptions;
    } else if (isUSTerritory(countryUpper)) {
      return territoryOptions.filter((t) => t.value === countryUpper);
    } else if (countryUpper === 'CA') {
      return canadaProvinceOptions;
    } else if (countryUpper === 'MX') {
      return mexicoStateOptions;
    }
    return [];
  };

  const getStateLabel = (country?: string) => {
    const countryUpper = country?.toUpperCase();
    if (countryUpper === 'US' || isUSTerritory(countryUpper)) {
      return 'State/U.S. Territory';
    } else if (countryUpper === 'CA') {
      return 'Province/Territory';
    } else if (countryUpper === 'MX') {
      return 'State';
    }
    return 'State/Province';
  };

  const primaryUSStateOptions = formData.primaryUSAddress?.country === CountryCode.US ? stateOptions : territoryOptions;

  return (
    <div className={classes.formContainer}>
      <Grid container direction="column" spacing={2}>
        <Grid item xs={12} className={classes.formField}>
          <RadioGroup
            label="Transferee Type"
            options={[
              {
                label: 'Transferee Entity',
                value: TransfereeType.ENTITY,
                dataTestId: `fincen-transferee-${index}-type-entity-radio`,
              },
              {
                label: 'Transferee Trust',
                value: TransfereeType.TRUST,
                dataTestId: `fincen-transferee-${index}-type-trust-radio`,
              },
            ]}
            onChange={(e) => {
              const updatedFormData = { ...formData, transfereeType: e.target.value as TransfereeType };
              setFormData(updatedFormData);
              onUpdate(updatedFormData, index);
            }}
            selected={{ value: formData.transfereeType }}
            row={true}
            compact={true}
            border={true}
            required={true}
            showValidation={formSubmitted}
            errs={validationErrors}
            name="transfereeType"
            data-testid={`fincen-transferee-${index}-type-radio`}
          />
          {getFieldError && getFieldError('transfereeType') && (
            <Typography variant="caption" color="error">
              {getFieldError('transfereeType')?.message}
            </Typography>
          )}
        </Grid>

        <Grid item xs={6}>
          <AgentNetTextInput
            label="Transferee Legal Name"
            name="transfereeLegalName"
            value={formData.transfereeLegalName || ''}
            variant="outlined"
            onChange={(e) => {
              handleFieldChange('transfereeLegalName', e.target.value);
            }}
            error={!!getFieldError('transfereeLegalName')}
            helperText={getFieldError('transfereeLegalName')?.message}
            required
            data-testid={`fincen-transferee-${index}-legal-name-input`}
            fullWidth
          />
        </Grid>

        {formData.transfereeType === TransfereeType.ENTITY && (
          <>
            {alternateNames.map((name, idx) => (
              <Grid item xs={6} key={idx}>
                <AgentNetTextInput
                  label="Alternate Name (i.e., trade name, DBA)"
                  name="transfereeDbaName"
                  value={name}
                  variant="outlined"
                  onChange={(e) => handleAlternateNameChange(idx, e.target.value)}
                  error={!!getFieldError('transfereeDbaName')}
                  helperText={getFieldError('transfereeDbaName')?.message}
                  data-testid={`fincen-transferee-${index}-alternate-name-${idx}-input`}
                  fullWidth
                />
              </Grid>
            ))}
          </>
        )}

        <div className={classes.sectionContainer}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.noConsiderationPaid}
                    size="small"
                    onChange={handleChange}
                    name="noConsiderationPaid"
                    color="primary"
                    className={classes.switch}
                    data-testid={`fincen-transferee-${index}-no-consideration-switch`}
                  />
                }
                label="No consideration paid"
                data-testid={`fincen-transferee-${index}-no-consideration-label`}
              />
            </Grid>
            <Grid item xs={6}>
              {!formData.noConsiderationPaid && (
                <CurrencyField
                  label="Total Consideration Paid, or to Be Paid, by or on Behalf of This Transferee"
                  name="totalConsideration"
                  fullWidth
                  value={Number(formData.totalConsideration) || 0}
                  onChange={(event: any) =>
                    handleFieldChange('totalConsideration', event.target.value?.toString() ?? '')
                  }
                  error={!!getFieldError('totalConsideration')}
                  helperText={getFieldError('totalConsideration')?.message}
                  required
                  data-testid={`fincen-transferee-${index}-total-consideration-input`}
                />
              )}
            </Grid>
          </Grid>

          {formData.transfereeType === TransfereeType.ENTITY && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControlLabel
                    className={classes.formControlLabel}
                    control={
                      <Switch
                        checked={formData.foreignBusinessNoUSLocation}
                        size="small"
                        onChange={(e) => {
                          const updatedFormData = {
                            ...formData,
                            foreignBusinessNoUSLocation: e.target.checked,
                            // When turning off foreign business toggle, default addressType to 'foreign' (Principal Place of Business U.S.)
                            // When turning on, clear the addressType
                            addressType: e.target.checked ? '' : formData.addressType || 'foreign',
                          };
                          setFormData(updatedFormData);
                          onUpdate(updatedFormData, index);
                        }}
                        name="foreignBusinessNoUSLocation"
                        color="primary"
                        className={classes.switch}
                        data-testid={`fincen-transferee-${index}-foreign-business-no-us-switch`}
                      />
                    }
                    label="Foreign principal place of business with no U.S. location"
                    data-testid={`fincen-transferee-${index}-foreign-business-no-us-label`}
                  />
                </Grid>
              </Grid>

              {formData.foreignBusinessNoUSLocation && (
                <div className={classes.addressContainer}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <GoogleAutocompleteSearch
                        label="Address 1"
                        variant="outlined"
                        name={`[${index}].foreignBusinessAddress.address1`}
                        value={formData.foreignBusinessAddress?.address1 || ''}
                        onChange={(e) => handleNestedChange('foreignBusinessAddress', 'address1', e.target.value)}
                        errs={effectiveValidationErrors}
                        showValidation={!!getFieldError('foreignBusinessAddress.address1')}
                        onPlaceSelect={(place: any) => {
                          const next = {
                            address1: place?.address1 ?? '',
                            address2: place?.address2 ?? '',
                            city: place?.city ?? '',
                            postalCode: place?.zipcode ?? '',
                            country: place?.country ?? formData.foreignBusinessAddress?.country,
                          };
                          const updatedFormData = {
                            ...formData,
                            foreignBusinessAddress: {
                              ...formData.foreignBusinessAddress,
                              ...next,
                            },
                          };
                          setFormData(updatedFormData);
                          onUpdate(updatedFormData, index);
                        }}
                        data-testid={`fincen-transferee-${index}-foreign-business-address1-input`}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <AgentNetTextInput
                        label="Address 2"
                        name="address2"
                        value={formData.foreignBusinessAddress?.address2 || ''}
                        onChange={(e) => handleNestedChange('foreignBusinessAddress', 'address2', e.target.value)}
                        error={!!getFieldError('foreignBusinessAddress.address2')}
                        helperText={getFieldError('foreignBusinessAddress.address2')?.message}
                        data-testid={`fincen-transferee-${index}-foreign-business-address2-input`}
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <AgentNetTextInput
                        label="City"
                        name="city"
                        value={formData.foreignBusinessAddress?.city || ''}
                        onChange={(e) => handleNestedChange('foreignBusinessAddress', 'city', e.target.value)}
                        error={!!getFieldError('foreignBusinessAddress.city')}
                        helperText={getFieldError('foreignBusinessAddress.city')?.message}
                        data-testid={`fincen-transferee-${index}-foreign-business-city-input`}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <AgentNetDropdownSelector
                        label="Country/Jurisdiction"
                        name={`[${index}].foreignBusinessAddress.country`}
                        value={formData.foreignBusinessAddress?.country || ''}
                        menuOption={(value: string) => handleNestedChange('foreignBusinessAddress', 'country', value)}
                        options={allCountryOptions}
                        data-testid={`fincen-transferee-${index}-foreign-business-country-select`}
                        errs={effectiveValidationErrors}
                        showValidation={formSubmitted || !!generateButtonClicked}
                        error={!!getFieldError('foreignBusinessAddress.country')}
                        variant="outlined"
                        fullWidth
                        required
                      />
                    </Grid>

                    {requiresStateAndPostalCode(formData.foreignBusinessAddress?.country) && (
                      <Grid item xs={3}>
                        <AgentNetDropdownSelector
                          label={getStateLabel(formData.foreignBusinessAddress?.country)}
                          name={`[${index}].foreignBusinessAddress.state`}
                          value={formData.foreignBusinessAddress?.state || ''}
                          menuOption={(value: string) => handleNestedChange('foreignBusinessAddress', 'state', value)}
                          options={getStateOptions(formData.foreignBusinessAddress?.country)}
                          data-testid={`fincen-transferee-${index}-foreign-business-state-select`}
                          errs={effectiveValidationErrors}
                          showValidation={formSubmitted || !!generateButtonClicked}
                          error={!!getFieldError('foreignBusinessAddress.state')}
                          variant="outlined"
                          fullWidth
                          required
                          disabled={isUSTerritory(formData.foreignBusinessAddress?.country)}
                        />
                      </Grid>
                    )}

                    <Grid item xs={3}>
                      {isUS(formData.foreignBusinessAddress?.country) ||
                      isUSTerritory(formData.foreignBusinessAddress?.country) ? (
                        <USPostalCodeField
                          label="Zip/Postal Code"
                          name="postalCode"
                          value={formData.foreignBusinessAddress?.postalCode || ''}
                          onChange={(e) => handleNestedChange('foreignBusinessAddress', 'postalCode', e.target.value)}
                          error={!!getFieldError('foreignBusinessAddress.postalCode')}
                          helperText={getFieldError('foreignBusinessAddress.postalCode')?.message}
                          data-testid={`fincen-transferee-${index}-foreign-business-postal-code-input`}
                          fullWidth
                          required={requiresStateAndPostalCode(formData.foreignBusinessAddress?.country)}
                        />
                      ) : (
                        <AgentNetTextInput
                          label="Zip/Postal Code"
                          name="postalCode"
                          value={formData.foreignBusinessAddress?.postalCode || ''}
                          onChange={(e) => handleNestedChange('foreignBusinessAddress', 'postalCode', e.target.value)}
                          error={!!getFieldError('foreignBusinessAddress.postalCode')}
                          helperText={getFieldError('foreignBusinessAddress.postalCode')?.message}
                          data-testid={`fincen-transferee-${index}-foreign-business-postal-code-input`}
                          fullWidth
                          required={requiresStateAndPostalCode(formData.foreignBusinessAddress?.country)}
                          inputProps={{ maxLength: 9 }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </div>
              )}
              {!formData.foreignBusinessNoUSLocation && (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <AgentNetDropdownSelector
                        label="Address Type"
                        name={`[${index}].addressType`}
                        value={formData.addressType || 'foreign'}
                        menuOption={(value: string) => handleDropdownChange('addressType', value)}
                        options={addressTypeOptions}
                        data-testid={`fincen-transferee-${index}-address-type-select`}
                        error={!!getFieldError('addressType')}
                        className={getFieldError('addressType') ? classes.dropdownError : ''}
                        variant="outlined"
                        fullWidth
                        required
                      />
                      {getFieldError('addressType') && (
                        <Typography variant="caption" color="error" className={classes.errorMessage}>
                          {getFieldError('addressType')?.message}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {formData.addressType === 'primary' && (
                    <>
                      <div className={classes.addressContainer}>
                        <Typography
                          variant="subtitle1"
                          className={classes.sectionHeader}
                          style={{ fontSize: pxToRem(16) }}
                        >
                          Foreign Principal Place of Business
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <GoogleAutocompleteSearch
                              label="Address 1"
                              name={`[${index}].foreignPrincipalAddress.address1`}
                              value={formData.foreignPrincipalAddress?.address1 || ''}
                              onChange={(e) =>
                                handleNestedChange('foreignPrincipalAddress', 'address1', e.target.value)
                              }
                              errs={effectiveValidationErrors}
                              showValidation={!!getFieldError('foreignPrincipalAddress.address1')}
                              onPlaceSelect={(place: any) => {
                                const next = {
                                  address1: place?.address1 ?? '',
                                  address2: place?.address2 ?? '',
                                  city: place?.city ?? '',
                                  postalCode: place?.zipcode ?? '',
                                  country: place?.country ?? formData.foreignPrincipalAddress?.country,
                                };
                                const updatedFormData = {
                                  ...formData,
                                  foreignPrincipalAddress: {
                                    ...formData.foreignPrincipalAddress,
                                    ...next,
                                  },
                                };
                                setFormData(updatedFormData);
                                onUpdate(updatedFormData, index);
                              }}
                              data-testid={`fincen-transferee-${index}-foreign-principal-address1-input`}
                              fullWidth
                              required
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <AgentNetTextInput
                              label="Address 2"
                              name="address2"
                              value={formData.foreignPrincipalAddress?.address2 || ''}
                              onChange={(e) =>
                                handleNestedChange('foreignPrincipalAddress', 'address2', e.target.value)
                              }
                              error={!!getFieldError('foreignPrincipalAddress.address2')}
                              helperText={getFieldError('foreignPrincipalAddress.address2')?.message}
                              data-testid={`fincen-transferee-${index}-foreign-principal-address2-input`}
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                          <Grid item xs={3}>
                            <AgentNetTextInput
                              label="City"
                              name="city"
                              value={formData.foreignPrincipalAddress?.city || ''}
                              onChange={(e) => handleNestedChange('foreignPrincipalAddress', 'city', e.target.value)}
                              error={!!getFieldError('foreignPrincipalAddress.city')}
                              helperText={getFieldError('foreignPrincipalAddress.city')?.message}
                              data-testid={`fincen-transferee-${index}-foreign-principal-city-input`}
                              fullWidth
                              required
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <AgentNetDropdownSelector
                              label="Country/Jurisdiction"
                              name={`[${index}].foreignPrincipalAddress.country`}
                              value={formData.foreignPrincipalAddress?.country || ''}
                              menuOption={(value: string) =>
                                handleNestedChange('foreignPrincipalAddress', 'country', value)
                              }
                              options={allCountryOptions}
                              data-testid={`fincen-transferee-${index}-foreign-principal-country-select`}
                              errs={effectiveValidationErrors}
                              showValidation={formSubmitted || !!generateButtonClicked}
                              error={!!getFieldError('foreignPrincipalAddress.country')}
                              variant="outlined"
                              fullWidth
                              required
                            />
                          </Grid>
                          {requiresStateAndPostalCode(formData.foreignPrincipalAddress?.country) && (
                            <Grid item xs={3}>
                              <AgentNetDropdownSelector
                                label={getStateLabel(formData.foreignPrincipalAddress?.country)}
                                name={`[${index}].foreignPrincipalAddress.state`}
                                value={formData.foreignPrincipalAddress?.state || ''}
                                menuOption={(value: string) =>
                                  handleNestedChange('foreignPrincipalAddress', 'state', value)
                                }
                                options={getStateOptions(formData.foreignPrincipalAddress?.country)}
                                data-testid={`fincen-transferee-${index}-foreign-principal-state-select`}
                                errs={effectiveValidationErrors}
                                showValidation={formSubmitted || !!generateButtonClicked}
                                error={!!getFieldError('foreignPrincipalAddress.state')}
                                variant="outlined"
                                fullWidth
                                required
                                disabled={isUSTerritory(formData.foreignPrincipalAddress?.country)}
                              />
                            </Grid>
                          )}
                          <Grid item xs={3}>
                            {isUS(formData.foreignPrincipalAddress?.country) ||
                            isUSTerritory(formData.foreignPrincipalAddress?.country) ? (
                              <USPostalCodeField
                                label="Zip/Postal Code"
                                name="postalCode"
                                value={formData.foreignPrincipalAddress?.postalCode || ''}
                                onChange={(e) =>
                                  handleNestedChange('foreignPrincipalAddress', 'postalCode', e.target.value)
                                }
                                error={!!getFieldError('foreignPrincipalAddress.postalCode')}
                                helperText={getFieldError('foreignPrincipalAddress.postalCode')?.message}
                                data-testid={`fincen-transferee-${index}-foreign-principal-postal-code-input`}
                                fullWidth
                                required={requiresStateAndPostalCode(formData.foreignPrincipalAddress?.country)}
                              />
                            ) : (
                              <AgentNetTextInput
                                label="Zip/Postal Code"
                                name="postalCode"
                                value={formData.foreignPrincipalAddress?.postalCode || ''}
                                onChange={(e) =>
                                  handleNestedChange('foreignPrincipalAddress', 'postalCode', e.target.value)
                                }
                                error={!!getFieldError('foreignPrincipalAddress.postalCode')}
                                helperText={getFieldError('foreignPrincipalAddress.postalCode')?.message}
                                data-testid={`fincen-transferee-${index}-foreign-principal-postal-code-input`}
                                fullWidth
                                required={requiresStateAndPostalCode(formData.foreignPrincipalAddress?.country)}
                                inputProps={{ maxLength: 9 }}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </div>

                      <div className={classes.addressContainer}>
                        <Typography
                          variant="subtitle1"
                          className={classes.sectionHeader}
                          style={{ fontSize: pxToRem(16) }}
                        >
                          Primary U.S. Location
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <GoogleAutocompleteSearch
                              label="Address 1"
                              name={`[${index}].primaryUSAddress.address1`}
                              value={formData.primaryUSAddress?.address1 || ''}
                              onChange={(e) => handleNestedChange('primaryUSAddress', 'address1', e.target.value)}
                              errs={effectiveValidationErrors}
                              showValidation={!!getFieldError('primaryUSAddress.address1')}
                              stateConstraint={
                                isUS(formData.primaryUSAddress?.country) && formData.primaryUSAddress?.state
                                  ? formData.primaryUSAddress.state
                                  : undefined
                              }
                              onPlaceSelect={(place: any) => {
                                const isUSCountry = isUS(formData.primaryUSAddress?.country);
                                const next = {
                                  address1: place?.address1 ?? '',
                                  address2: place?.address2 ?? '',
                                  city: place?.city ?? '',
                                  postalCode: place?.zipcode ?? '',
                                  country: place?.country ?? formData.primaryUSAddress?.country,
                                  state: isUSCountry
                                    ? place?.state ?? formData.primaryUSAddress?.state ?? ''
                                    : formData.primaryUSAddress?.state,
                                };
                                const updatedFormData = {
                                  ...formData,
                                  primaryUSAddress: {
                                    ...formData.primaryUSAddress,
                                    ...next,
                                  },
                                };
                                setFormData(updatedFormData);
                                onUpdate(updatedFormData, index);
                              }}
                              data-testid={`fincen-transferee-${index}-primary-us-address1-input`}
                              fullWidth
                              required
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <AgentNetTextInput
                              label="Address 2"
                              name="address2"
                              value={formData.primaryUSAddress?.address2 || ''}
                              onChange={(e) => handleNestedChange('primaryUSAddress', 'address2', e.target.value)}
                              error={!!getFieldError('primaryUSAddress.address2')}
                              helperText={getFieldError('primaryUSAddress.address2')?.message}
                              data-testid={`fincen-transferee-${index}-primary-us-address2-input`}
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                          <Grid item xs={3}>
                            <AgentNetTextInput
                              label="City"
                              name="city"
                              value={formData.primaryUSAddress?.city || ''}
                              onChange={(e) => handleNestedChange('primaryUSAddress', 'city', e.target.value)}
                              error={!!getFieldError('primaryUSAddress.city')}
                              helperText={getFieldError('primaryUSAddress.city')?.message}
                              data-testid={`fincen-transferee-${index}-primary-us-city-input`}
                              fullWidth
                              required
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <AgentNetDropdownSelector
                              label="Country/Jurisdiction"
                              name={`[${index}].primaryUSAddress.country`}
                              value={formData.primaryUSAddress?.country || ''}
                              menuOption={(value: string) => handleNestedChange('primaryUSAddress', 'country', value)}
                              options={allCountryOptions}
                              data-testid={`fincen-transferee-${index}-primary-us-country-select`}
                              errs={effectiveValidationErrors}
                              showValidation={formSubmitted || !!generateButtonClicked}
                              error={!!getFieldError('primaryUSAddress.country')}
                              variant="outlined"
                              defaultToFirst={false}
                              fullWidth
                              required
                            />
                          </Grid>
                          {requiresStateAndPostalCode(formData.primaryUSAddress?.country) && (
                            <Grid item xs={3}>
                              <AgentNetDropdownSelector
                                label={getStateLabel(formData.primaryUSAddress?.country)}
                                name={`[${index}].primaryUSAddress.state`}
                                value={formData.primaryUSAddress?.state || ''}
                                menuOption={(value: string) => handleNestedChange('primaryUSAddress', 'state', value)}
                                options={getStateOptions(formData.primaryUSAddress?.country)}
                                data-testid={`fincen-transferee-${index}-primary-us-state-select`}
                                errs={effectiveValidationErrors}
                                showValidation={formSubmitted || !!generateButtonClicked}
                                error={!!getFieldError('primaryUSAddress.state')}
                                variant="outlined"
                                fullWidth
                                required
                                disabled={isUSTerritory(formData.primaryUSAddress?.country)}
                              />
                            </Grid>
                          )}
                          <Grid item xs={3}>
                            {isUS(formData.primaryUSAddress?.country) ||
                            isUSTerritory(formData.primaryUSAddress?.country) ? (
                              <USPostalCodeField
                                label="Zip/Postal Code"
                                name="postalCode"
                                value={formData.primaryUSAddress?.postalCode || ''}
                                onChange={(e) => handleNestedChange('primaryUSAddress', 'postalCode', e.target.value)}
                                error={!!getFieldError('primaryUSAddress.postalCode')}
                                helperText={getFieldError('primaryUSAddress.postalCode')?.message}
                                data-testid={`fincen-transferee-${index}-primary-us-postal-code-input`}
                                fullWidth
                                required={requiresStateAndPostalCode(formData.primaryUSAddress?.country)}
                              />
                            ) : (
                              <AgentNetTextInput
                                label="Zip/Postal Code"
                                name="postalCode"
                                value={formData.primaryUSAddress?.postalCode || ''}
                                onChange={(e) => handleNestedChange('primaryUSAddress', 'postalCode', e.target.value)}
                                error={!!getFieldError('primaryUSAddress.postalCode')}
                                helperText={getFieldError('primaryUSAddress.postalCode')?.message}
                                data-testid={`fincen-transferee-${index}-primary-us-postal-code-input`}
                                fullWidth
                                required={requiresStateAndPostalCode(formData.primaryUSAddress?.country)}
                                inputProps={{ maxLength: 9 }}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </div>
                    </>
                  )}
                  {formData.addressType === 'foreign' && (
                    <div className={classes.addressContainer}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <GoogleAutocompleteSearch
                            label="Address 1"
                            name={`[${index}].primaryUSAddress.address1`}
                            value={formData.primaryUSAddress?.address1 || ''}
                            onChange={(e) => handleNestedChange('primaryUSAddress', 'address1', e.target.value)}
                            errs={effectiveValidationErrors}
                            showValidation={!!getFieldError('primaryUSAddress.address1')}
                            stateConstraint={
                              isUS(formData.primaryUSAddress?.country) && formData.primaryUSAddress?.state
                                ? formData.primaryUSAddress.state
                                : undefined
                            }
                            onPlaceSelect={(place: any) => {
                              const isUSCountry = isUS(formData.primaryUSAddress?.country);
                              const next = {
                                address1: place?.address1 ?? '',
                                address2: place?.address2 ?? '',
                                city: place?.city ?? '',
                                postalCode: place?.zipcode ?? '',
                                country: place?.country ?? formData.primaryUSAddress?.country,
                                state: isUSCountry
                                  ? place?.state ?? formData.primaryUSAddress?.state ?? ''
                                  : formData.primaryUSAddress?.state,
                              };
                              const updatedFormData = {
                                ...formData,
                                primaryUSAddress: {
                                  ...formData.primaryUSAddress,
                                  ...next,
                                },
                              };
                              setFormData(updatedFormData);
                              onUpdate(updatedFormData, index);
                            }}
                            data-testid={`fincen-transferee-${index}-primary-us-address1-input`}
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <AgentNetTextInput
                            label="Address 2"
                            name="address2"
                            value={formData.primaryUSAddress?.address2 || ''}
                            onChange={(e) => handleNestedChange('primaryUSAddress', 'address2', e.target.value)}
                            error={!!getFieldError('primaryUSAddress.address2')}
                            helperText={getFieldError('primaryUSAddress.address2')?.message}
                            data-testid={`fincen-transferee-${index}-primary-us-address2-input`}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <AgentNetTextInput
                            label="City"
                            name="city"
                            value={formData.primaryUSAddress?.city || ''}
                            onChange={(e) => handleNestedChange('primaryUSAddress', 'city', e.target.value)}
                            error={!!getFieldError('primaryUSAddress.city')}
                            helperText={getFieldError('primaryUSAddress.city')?.message}
                            data-testid={`fincen-transferee-${index}-primary-us-city-input`}
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <AgentNetDropdownSelector
                            label="Country/Jurisdiction"
                            name={`[${index}].primaryUSAddress.country`}
                            value={formData.primaryUSAddress?.country || ''}
                            menuOption={(value: string) => handleNestedChange('primaryUSAddress', 'country', value)}
                            options={allCountryOptions}
                            data-testid={`fincen-transferee-${index}-primary-us-country-select`}
                            errs={effectiveValidationErrors}
                            showValidation={formSubmitted || !!generateButtonClicked}
                            variant="outlined"
                            defaultToFirst={false}
                            fullWidth
                            required
                          />
                        </Grid>
                        {requiresStateAndPostalCode(formData.primaryUSAddress?.country) && (
                          <Grid item xs={3}>
                            <AgentNetDropdownSelector
                              label={getStateLabel(formData.primaryUSAddress?.country)}
                              name={`[${index}].primaryUSAddress.state`}
                              value={formData.primaryUSAddress?.state || ''}
                              menuOption={(value: string) => handleNestedChange('primaryUSAddress', 'state', value)}
                              options={getStateOptions(formData.primaryUSAddress?.country)}
                              data-testid={`fincen-transferee-${index}-primary-us-state-select`}
                              errs={effectiveValidationErrors}
                              showValidation={formSubmitted || !!generateButtonClicked}
                              error={!!getFieldError('primaryUSAddress.state')}
                              variant="outlined"
                              fullWidth
                              required
                              disabled={isUSTerritory(formData.primaryUSAddress?.country)}
                            />
                          </Grid>
                        )}
                        <Grid item xs={3}>
                          {isUS(formData.primaryUSAddress?.country) ||
                          isUSTerritory(formData.primaryUSAddress?.country) ? (
                            <USPostalCodeField
                              label="Zip/Postal Code"
                              name="zipCode"
                              value={formData.primaryUSAddress?.postalCode || ''}
                              onChange={(e) => handleNestedChange('primaryUSAddress', 'postalCode', e.target.value)}
                              error={!!getFieldError('primaryUSAddress.postalCode')}
                              helperText={getFieldError('primaryUSAddress.postalCode')?.message}
                              data-testid={`fincen-transferee-${index}-primary-us-postal-code-input`}
                              fullWidth
                              required={requiresStateAndPostalCode(formData.primaryUSAddress?.country)}
                            />
                          ) : (
                            <AgentNetTextInput
                              label="Zip/Postal Code"
                              name="zipCode"
                              value={formData.primaryUSAddress?.postalCode || ''}
                              onChange={(e) => handleNestedChange('primaryUSAddress', 'postalCode', e.target.value)}
                              error={!!getFieldError('primaryUSAddress.postalCode')}
                              helperText={getFieldError('primaryUSAddress.postalCode')?.message}
                              data-testid={`fincen-transferee-${index}-primary-us-postal-code-input`}
                              fullWidth
                              required={requiresStateAndPostalCode(formData.primaryUSAddress?.country)}
                              inputProps={{ maxLength: 9 }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div className={classes.sectionContainer}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" className={classes.sectionHeader} style={{ fontSize: pxToRem(16) }}>
                Form of Identification and Issuing Jurisdiction
              </Typography>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <AgentNetDropdownSelector
                  label="Type of Unique Identifying Number"
                  name={`[${index}].identificationType`}
                  value={formData.identificationType}
                  menuOption={(value: string) => handleDropdownChange('identificationType', value)}
                  options={getIdentificationTypeOptions(formData.transfereeType)}
                  data-testid={`fincen-transferee-${index}-identification-type-select`}
                  error={!!getFieldError('identificationType')}
                  className={getFieldError('identificationType') ? classes.dropdownError : ''}
                  variant="outlined"
                  fullWidth
                  required
                />
                {getFieldError('identificationType') && (
                  <Typography variant="caption" color="error" className={classes.errorMessage}>
                    {getFieldError('identificationType')?.message}
                  </Typography>
                )}
              </Grid>

              {formData.identificationType && formData.identificationType !== 'noIdentification' && (
                <Grid item xs={6}>
                  <IdentificationNumberField
                    identificationType={formData.identificationType}
                    value={formData.identificationNumber || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleFieldChange('identificationNumber', e.target.value)
                    }
                    error={!!getFieldError('identificationNumber')}
                    helperText={getFieldError('identificationNumber')?.message}
                    name="identificationNumber"
                    data-testid={`fincen-transferee-${index}-identification-number-input`}
                    keyPrefix={index.toString()}
                    required
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>

            {formData.identificationType &&
              (formData.identificationType === 'foreignTaxId' ||
                formData.identificationType === 'foreignPassport' ||
                formData.identificationType === 'foreignEntityRegistrationNumber') && (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <AgentNetDropdownSelector
                        label="Issuing Jurisdiction(if foreign)"
                        name={`[${index}].issuingJurisdiction`}
                        value={formData.issuingJurisdiction || ''}
                        menuOption={(value: string) => handleDropdownChange('issuingJurisdiction', value)}
                        options={foreignTerritoryOptions}
                        data-testid={`fincen-transferee-${index}-issuing-jurisdiction-select`}
                        errs={effectiveValidationErrors}
                        showValidation={formSubmitted || !!generateButtonClicked}
                        error={!!getFieldError('issuingJurisdiction')}
                        className={getFieldError('issuingJurisdiction') ? classes.dropdownError : ''}
                        variant="outlined"
                        required
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </>
              )}

            {formData.transfereeType === TransfereeType.TRUST && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DateFieldString
                      label="Date Trust Instrument was Executed"
                      format="MM/dd/yyyy"
                      value={formData.dateTrustExecuted || ''}
                      onChange={handleDateTrustExecutedChange}
                      variant="outlined"
                      fullWidth
                      qaAttribute={`fincen-transferee-${index}-date-trust-executed-input`}
                      placeholder="mm/dd/yyyy"
                      error={!!getFieldError('dateTrustExecuted')}
                      helperText={getFieldError('dateTrustExecuted')?.message}
                      showValidation={true}
                      shouldPrioritizeOnChange={false}
                      required
                    />
                  </Grid>
                  <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isRevocableTrust || false}
                          onChange={(e) => {
                            const updatedFormData = { ...formData, isRevocableTrust: e.target.checked };
                            setFormData(updatedFormData);
                            onUpdate(updatedFormData, index);
                          }}
                          name="isRevocableTrust"
                          data-testid={`fincen-transferee-${index}-is-revocable-trust-checkbox`}
                        />
                      }
                      label="Revocable Trust"
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </div>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={3}>
            <Typography variant="h6" className={classes.sectionHeader}>
              Parties Associated with this Transferee
              <Tooltip title="Additional information about parties associated with this transferee">
                <HelpOutline fontSize="small" className={classes.infoIcon} />
              </Tooltip>
            </Typography>

            <AgentNetButton
              variant="contained"
              color="primary"
              size="small"
              className={classes.addButton}
              data-testid={`fincen-transferee-${index}-add-party-button`}
              onClick={handleAddParty}
              plusIcon
              disabled={formData.parties?.length >= 8}
            >
              Add Party
            </AgentNetButton>
          </Box>

          {formData.parties?.map((party, partyIndex) => {
            const partyId = party.id || `party-${index}-${partyIndex}`;
            return (
              <Box mb={2} key={partyId}>
                <PartyAccordion
                  party={party}
                  transfereeType={formData.transfereeType}
                  transfereeIndex={index}
                  partyIndex={partyIndex}
                  expanded={expandedPartyId === partyId}
                  onExpand={() => setExpandedPartyId(expandedPartyId === partyId ? null : partyId)}
                  onUpdate={handleUpdateParty}
                  onDelete={handleDeleteParty}
                  validationErrors={effectiveValidationErrors}
                  clearValidationErrors={clearValidationErrors}
                />
              </Box>
            );
          })}
        </div>
      </Grid>
    </div>
  );
};

export default TransfereeForm;
